import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "@/hooks/useTranslation";
import { getRsvpFunnel, type RsvpFunnelCounts, type RsvpFunnelSummary } from "@/services/api/rsvpFunnel";

const DAYS = 30;

const STAGES: Array<{ key: keyof RsvpFunnelCounts; label: string }> = [
  { key: "opened", label: "guests.funnel.opened" },
  { key: "fieldTouched", label: "guests.funnel.fieldTouched" },
  { key: "draftRestored", label: "guests.funnel.draftRestored" },
  { key: "submitted", label: "guests.funnel.submitted" },
];

/**
 * JUB-67: the owner's view of the RSVP-form interaction counts. Raw totals
 * only, with a note that they count interactions rather than people — the
 * stages are not linked to each other, so no rates are shown.
 */
export function RsvpFunnelCard({ eventUuid }: { eventUuid: string }) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<RsvpFunnelSummary | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getRsvpFunnel(eventUuid, DAYS)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [eventUuid]);

  if (summary && !summary.enabled) return null;

  return (
    <Card data-testid="rsvp-funnel-card">
      <CardHeader>
        <CardTitle className="text-lg">{t("guests.funnel.title")}</CardTitle>
        <CardDescription>{t("guests.funnel.description", { days: summary?.days ?? DAYS })}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {failed ? (
          <p className="text-sm text-muted-foreground">{t("guests.funnel.error")}</p>
        ) : (
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {STAGES.map(({ key, label }) => (
              <div key={key} className="rounded-lg border p-3">
                <dt className="text-xs text-muted-foreground">{t(label)}</dt>
                <dd className="text-2xl font-semibold" data-testid={`rsvp-funnel-${key}`}>
                  {summary ? summary.counts[key] : "–"}
                </dd>
              </div>
            ))}
          </dl>
        )}
        <p className="text-xs text-muted-foreground" data-testid="rsvp-funnel-note">
          {t("guests.funnel.note")}
        </p>
      </CardContent>
    </Card>
  );
}
